import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { requestMatchingGet, requestMatchingReceipt } from "../../api/Companies.api";
import { MatchingRequestResponse } from "../../model/MatchingRequest";
import Button from "../../components/button/Button";
import { Spiner } from "../../components/spinner/Spiner";

const initRequestData = {
  requestId: '',
  createAt: new Date(),
  updateAt: new Date(),
  reqStatus: 0,
  reqUserId: '',
  reqContents: '',
  reqArea: ''
}

export const MatchingReceipt = () => {
  const {tenantId, hash, requestId} = useParams<{tenantId: string, hash: string, requestId: string}>();

  const [requestData, setRequestData] = useState<MatchingRequestResponse>(initRequestData);
  const [isLoad, setIsLoad] = useState<boolean>(false);

  const onReceipt = async () => {
    try {
      setIsLoad(true);
      if(!tenantId || !hash || !requestId){
        throw new Error();
      }
      await requestMatchingReceipt(tenantId, hash, requestId)
        .then((resolve) => {
          if(resolve.success){
            setRequestData(resolve.data);
          } else {
            errorDisplaySet('依頼登録に失敗しました。')
          }
        })
        .catch((reject) => {
          console.error(reject.data.message);
          errorDisplaySet('依頼登録に失敗しました。');
        });
    } catch (err: any) {
      console.error(err);
      errorDisplaySet(err)
    }
    setIsLoad(false);
  }

  const errorDisplaySet = (message: string) => {
    const requestData = {
      requestId: undefined,
      createAt: new Date(),
      updateAt: new Date(),
      reqStatus: 0,
      reqUserId: '',
      reqContents: '',
      reqArea: '',
      message
    }
    setRequestData(requestData);
  };

  useEffect(() => {
    const f = async () => {
      try{
        if(!tenantId || !hash || !requestId){
          throw new Error('通信エラーが発生しました。担当者へご連絡ください。')
        }
        await requestMatchingGet(tenantId, hash, requestId)
          .then((resolve) => {
            setRequestData(resolve.data);
          })
          .catch((reject) => {
            throw new Error(reject.data.message)
          })
      } catch (err: any) {
        console.error(err.message);
        errorDisplaySet(err.message)
      }
    };
    f();
  },[hash, requestId, tenantId])

  // 画面を作成
  if(requestData?.requestId === '') {
    return (<MatchingReceiptFrame>
      <Spiner />
    </MatchingReceiptFrame>);
  } else if(requestData?.requestId === undefined) {
    // エラー
    return (<MatchingReceiptFrame>
      <p>エラー : {requestData?.message}</p>
      <p>requestId = {requestId}</p>
    </MatchingReceiptFrame>);
  } else if(requestData.reqStatus === 0) {
    // ボランティア未決定
    return (<MatchingReceiptFrame>
      {isLoad ? (<Spiner />) : (<>
        <h1>ボランティア依頼があります。</h1>
        <section>
          <dl className="flex flex-col space-y-4">
            <dt>依頼者(ID: {requestData.reqUserId})</dt>
            <dd className="flex text-3xl font-black items-end justify-center">
              <ruby>
                {requestData.reqUserName}
                <rp>(</rp><rt>{requestData.reqUserNameKana}</rt><rp>)</rp>
              </ruby>
              <span>さん</span>
            </dd>
          </dl>
        </section>
        <section>
          <ul className="flex flex-col space-y-4">
            <li className="flex space-x-2 items-center">
              <span className="whitespace-nowrap w-[4.5rem]">内容 :</span>
              <span className="border-2 border-black rounded-xl p-2 font-black text-2xl whitespace-pre">{requestData.reqContentsDisp?.replace('\\n', '\n')}</span>
            </li>
            <li className="flex space-x-2 items-center">
              <span className="whitespace-nowrap w-[4.5rem]">エリア :</span>
              <span className="border-2 border-black rounded-xl p-2 font-black text-2xl whitespace-pre">{requestData.reqAreaDisp?.replace('\\n', '\n')}</span>
            </li>
          </ul>
        </section>
        <section className="flex flex-col space-y-4 w-full items-center">
          <h3 className="font-black">支援しますか？</h3>
          <Button
            children={"支援する"}
            color="orange"
            className="w-full"
            onClick={onReceipt}
          />
        </section>
      </>)}
    </MatchingReceiptFrame>);
  } else if(requestData.recUserId === requestData.currentlyConnectedUserId) {
    // ボランティアが自分
    return (<MatchingReceiptFrame>
      <h1>あなたにボランティアが確定しました。</h1>
      <section>
        <dl className="flex flex-col space-y-4">
          <dt>依頼者(ID: {requestData.reqUserId})</dt>
          <dd className="flex text-3xl font-black items-end justify-center">
            <ruby>
              {requestData.reqUserName}
              <rp>(</rp><rt>{requestData.reqUserNameKana}</rt><rp>)</rp>
            </ruby>
            <span>さん</span>
          </dd>
        </dl>
      </section>
      <section>
        <ul className="flex flex-col space-y-4">
          <li className="flex space-x-2 items-center">
            <span className="whitespace-nowrap w-[4.5rem]">内容 :</span>
            <span className="border-2 border-black rounded-xl p-2 font-black text-2xl whitespace-pre">{requestData.reqContentsDisp?.replace('\\n', '\n')}</span>
          </li>
          <li className="flex space-x-2 items-center">
            <span className="whitespace-nowrap w-[4.5rem]">エリア :</span>
            <span className="border-2 border-black rounded-xl p-2 font-black text-2xl whitespace-pre">{requestData.reqAreaDisp?.replace('\\n', '\n')}</span>
          </li>
        </ul>
      </section>
      <section className="flex flex-col space-y-4">
        <ul className="flex flex-col space-y-4">
          <li className="flex space-x-2 items-center">
            <span className="whitespace-nowrap w-[6rem]">電話番号 :</span>
            <a
              className="px-4 py-2 text-2xl text-white bg-blue-600 focus:ring-2 focus:ring-offset-2 hover:bg-blue-700 focus:ring-green-500 focus:ring-offset-green-200 rounded-xl"
              href={`tel://${requestData.phoneNumber}`}
            >
              {requestData.phoneNumber}
            </a>
          </li>
        </ul>
        <p>依頼者に連絡してください。</p>
      </section>
    </MatchingReceiptFrame>);
  } else if(requestData.recUserId !== requestData.currentlyConnectedUserId) {
    // ボランティアが他人
    return (<MatchingReceiptFrame>
      <h1>すでに他のボランティアが決まっています。</h1>
      <section>
        <dl className="flex flex-col space-y-4">
          <dt>ボランティア</dt>
          <dd className="flex text-3xl font-black items-end justify-center">
            <ruby>
              {requestData.recUserName}
              <rp>(</rp><rt>{requestData.recUserNameKana}</rt><rp>)</rp>
            </ruby>
            <span>さん</span>
          </dd>
        </dl>
      </section>
      <section>
        <dl className="flex flex-col space-y-4">
          <dt>依頼者</dt>
          <dd className="flex text-3xl font-black items-end justify-center">
            <ruby>
              {requestData.reqUserName}
              <rp>(</rp><rt>{requestData.reqUserNameKana}</rt><rp>)</rp>
            </ruby>
            <span>さん</span>
          </dd>
        </dl>
      </section>
    </MatchingReceiptFrame>);
  }
  return (<MatchingReceiptFrame>
    リクエスト中
  </MatchingReceiptFrame>);
}

// 外枠
type MatchingReceiptFrameProps = {
  children: React.ReactNode;
}

const MatchingReceiptFrame = ({children}: MatchingReceiptFrameProps) => {
  return (
    <div className="flex min-h-full items-center p-4 text-xl">
      <article className="flex-flex-col space-y-6 w-full">
        {children}
      </article>
    </div>
  );
}